import { entityTableSegment } from "../warehouse/targetTableName";
import { sameJsonb } from "../sameJsonb";

/**
 * #2428: a Google Analytics 4 report a person adds to a connection. One report is one hand-named
 * entity (`dataflow_entities`, instance 0043) whose `definition` says what the image asks Google
 * for, and it lands in its own table, `<connection name>_<report name>`
 * (`shared/warehouse/targetTableName.ts`). The seed names the kind (`entitySelection.definitionKind`
 * `ga4_report`), and the server, the Add report drawer and the tests all read this one module.
 *
 * Pure: no I/O, no clock. The rules are the image's own (skyConnect `source-ga4`
 * `reports/config.py` `REPORT_NAME_RE`, the 9 dimension and 10 metric limits, a name in one list
 * only), checked at save so a run never meets them. Whether a name exists on the property is
 * Google's to say, at the first run (DESIGN.md 2.3): there is no metadata check here, and
 * `ga4ReportProblems` returns `{ field, code, sentence }` so a later check can merge into it.
 *
 * Every sentence below is customer copy: `scripts/check-copy.mjs` scans this file.
 */

export const GA4_REPORT_KIND = "ga4_report" as const;

export const GA4_DIMENSIONS_MAX = 9;
export const GA4_METRICS_MAX = 10;
export const GA4_FIELD_NAME_MAX = 100;
export const GA4_FILTER_MAX = 4096;
/** The `dataflow_entities.definition` CHECK (instance 0043), in bytes of its text. */
export const GA4_DEFINITION_BYTES_MAX = 8192;
/** How deep a filter's groups may nest: a leaf at the top is depth 1. */
export const GA4_FILTER_DEPTH_MAX = 5;

const REPORT_NAME_RE = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
const FIELD_NAME_RE = /^[A-Za-z][A-Za-z0-9_]*(?::[A-Za-z0-9_]+)?$/;
const PROPERTY_ID_RE = /^[0-9]{1,20}$/;

export const GA4_MATCH_TYPES = [
  "EXACT",
  "BEGINS_WITH",
  "ENDS_WITH",
  "CONTAINS",
  "FULL_REGEXP",
  "PARTIAL_REGEXP",
] as const;
export type Ga4MatchType = (typeof GA4_MATCH_TYPES)[number];

export const GA4_NUMERIC_OPERATIONS = [
  "EQUAL",
  "LESS_THAN",
  "LESS_THAN_OR_EQUAL",
  "GREATER_THAN",
  "GREATER_THAN_OR_EQUAL",
] as const;

const EXPRESSION_KEYS = ["filter", "andGroup", "orGroup", "notExpression"] as const;
const LEAF_KINDS = ["stringFilter", "inListFilter", "numericFilter", "betweenFilter"] as const;

/** A GA4 Data API FilterExpression, stored parsed. Its shape is checked by `filterProblem`. */
export type Ga4FilterExpression = Record<string, unknown>;

/** What a report asks Google for, as stored (`kind` included) and as the bundle sends it (without). */
export interface Ga4ReportDefinition {
  kind: typeof GA4_REPORT_KIND;
  propertyId: string;
  dimensions: string[];
  metrics: string[];
  dimensionFilter?: Ga4FilterExpression;
}

export type Ga4ReportField = "name" | "propertyId" | "dimensions" | "metrics" | "dimensionFilter";

export interface Ga4ReportProblem {
  field: Ga4ReportField;
  code: string;
  sentence: string;
}

export interface Ga4ReportContext {
  /** The connection's property ids: saved, or in the same request. */
  propertyIds: readonly string[];
  /** The other reports' names in this connection (not this one's own). */
  otherNames: readonly string[];
}

export const GA4_REPORT_SENTENCES = {
  nameRule: "Use letters, numbers and underscores, starting with a letter, at most 64 characters.",
  nameTaken: "Another report in this connection lands in the same table.",
  propertyRequired: "Choose the property this report reads.",
  propertyRule: "A property ID is digits only.",
  propertyNotListed: (id: string) =>
    `Property ${id} is not one of this connection's property IDs. Add it to GA4 Property IDs first.`,
  dimensionsRequired: "Add at least one dimension.",
  dimensionsTooMany: `A report has at most ${GA4_DIMENSIONS_MAX} dimensions.`,
  metricsRequired: "Add at least one metric.",
  metricsTooMany: `A report has at most ${GA4_METRICS_MAX} metrics.`,
  fieldNameRule: (name: string) =>
    `${name} is not a GA4 API name. Use letters, numbers and underscores, for example sessionSourceMedium or customEvent:plan_type.`,
  fieldNameTooLong: `A dimension or metric name is at most ${GA4_FIELD_NAME_MAX} characters.`,
  repeated: (name: string) => `${name} is listed twice.`,
  inBoth: (name: string) =>
    `${name} is listed as a dimension and as a metric. Keep it in one list.`,
  columnClash: (a: string, b: string) =>
    `${a} and ${b} land in the same table column. Keep one of them.`,
  reservedColumn: (name: string) =>
    `${name} lands in a column the import adds itself. Choose another name.`,
  filterTooLong: `A filter is at most ${GA4_FILTER_MAX} characters.`,
  filterNotJson: "This filter is not valid JSON. Check the brackets, quotes and commas.",
  filterShape:
    "This filter is not a GA4 filter. Use filter, andGroup, orGroup or notExpression at each level.",
  filterTooDeep: `A filter nests at most ${GA4_FILTER_DEPTH_MAX} levels.`,
  filterLeaf:
    "Each condition names a fieldName and one of stringFilter, inListFilter, numericFilter or betweenFilter.",
  filterMatchType:
    "A stringFilter matchType is EXACT, BEGINS_WITH, ENDS_WITH, CONTAINS, FULL_REGEXP or PARTIAL_REGEXP.",
  filterValue: "A condition needs a value to compare with.",
  filterNumber: "A numeric condition compares with a number.",
  definitionShape: "This report is not complete. Choose a property, dimensions and metrics.",
  definitionTooLarge: "This report is too large to save. Shorten the filter.",
  required: "Add the report again with its property, dimensions and metrics.",
} as const;

/** The table columns the image adds to every report (skyConnect `reports/entity_manifest.py`). */
const LOAD_COLUMNS = ["_property_id", "_report_name", "_data_date", "_run_id", "_extracted_at"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The column a GA4 name lands in under the load's column rule (`runner/snowflake/identifiers.ts`
 * `cleanIdentifier` without its de-duplication: characters outside `[A-Za-z0-9_]` deleted, lower
 * case, `_` before a name that starts with a digit, `_col` for an empty one, another `_` before
 * skyData's own `_skydata_` prefix; no `CDM` prefix since 2026-10-31). The image names each column
 * as GA4's header spells it (`customEvent:plan_type`), so two names that fold to one column would
 * be renamed `_2` silently by the load: refused at save instead. The runner's parity test holds
 * the two rules equal.
 */
export function ga4ColumnOf(name: string): string {
  let cleaned = name.replace(/[^A-Za-z0-9_]+/g, "").toLowerCase();
  if (cleaned === "") cleaned = "_col";
  else if (/^[0-9]/.test(cleaned)) cleaned = `_${cleaned}`;
  if (cleaned.startsWith("_skydata_")) cleaned = `_${cleaned}`;
  return cleaned;
}

/** A list typed or pasted: split on commas and new lines, trimmed, no empties. */
export function splitGa4NameList(text: string): string[] {
  return text
    .split(/[,\n\r]+/)
    .map((part) => part.trim())
    .filter((part) => part !== "");
}

/** The connection's `propertyIds` field (digits separated by commas or spaces), in order, once each. */
export function parseGa4PropertyIds(value: string | null | undefined): string[] {
  const ids = (value ?? "")
    .split(/[\s,]+/)
    .map((part) => part.trim())
    .filter((part) => part !== "");
  return [...new Set(ids)];
}

export type ParsedFilter =
  { ok: true; filter: Ga4FilterExpression | null } | { ok: false; sentence: string };

/** The filter box's text: empty or `{}` is no filter, anything else must parse to an object. */
export function parseGa4FilterText(text: string): ParsedFilter {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: true, filter: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, sentence: GA4_REPORT_SENTENCES.filterNotJson };
  }
  if (!isRecord(parsed)) return { ok: false, sentence: GA4_REPORT_SENTENCES.filterShape };
  if (Object.keys(parsed).length === 0) return { ok: true, filter: null };
  return { ok: true, filter: parsed };
}

function isNumericValue(value: unknown): boolean {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value);
  if (keys.length !== 1) return false;
  if ("int64Value" in value) {
    const v = value.int64Value;
    return (
      (typeof v === "string" && /^-?[0-9]{1,19}$/.test(v)) ||
      (typeof v === "number" && Number.isInteger(v))
    );
  }
  if ("doubleValue" in value) return typeof value.doubleValue === "number";
  return false;
}

function onlyKeys(record: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(record).every((key) => allowed.includes(key));
}

function caseSensitiveProblem(body: Record<string, unknown>): string | null {
  return body.caseSensitive !== undefined && typeof body.caseSensitive !== "boolean"
    ? GA4_REPORT_SENTENCES.filterLeaf
    : null;
}

function stringFilterProblem(body: Record<string, unknown>): string | null {
  if (!onlyKeys(body, ["matchType", "value", "caseSensitive"])) {
    return GA4_REPORT_SENTENCES.filterLeaf;
  }
  if (
    body.matchType !== undefined &&
    !(GA4_MATCH_TYPES as readonly unknown[]).includes(body.matchType)
  ) {
    return GA4_REPORT_SENTENCES.filterMatchType;
  }
  if (typeof body.value !== "string") return GA4_REPORT_SENTENCES.filterValue;
  return caseSensitiveProblem(body);
}

function inListFilterProblem(body: Record<string, unknown>): string | null {
  if (!onlyKeys(body, ["values", "caseSensitive"])) return GA4_REPORT_SENTENCES.filterLeaf;
  const values = body.values;
  if (
    !Array.isArray(values) ||
    values.length === 0 ||
    !values.every((value) => typeof value === "string")
  ) {
    return GA4_REPORT_SENTENCES.filterValue;
  }
  return caseSensitiveProblem(body);
}

function numericFilterProblem(body: Record<string, unknown>): string | null {
  if (!onlyKeys(body, ["operation", "value"])) return GA4_REPORT_SENTENCES.filterLeaf;
  if (!(GA4_NUMERIC_OPERATIONS as readonly unknown[]).includes(body.operation)) {
    return GA4_REPORT_SENTENCES.filterLeaf;
  }
  return isNumericValue(body.value) ? null : GA4_REPORT_SENTENCES.filterNumber;
}

function betweenFilterProblem(body: Record<string, unknown>): string | null {
  if (!onlyKeys(body, ["fromValue", "toValue"])) return GA4_REPORT_SENTENCES.filterLeaf;
  return isNumericValue(body.fromValue) && isNumericValue(body.toValue)
    ? null
    : GA4_REPORT_SENTENCES.filterNumber;
}

const LEAF_BODY_PROBLEM: Record<
  (typeof LEAF_KINDS)[number],
  (body: Record<string, unknown>) => string | null
> = {
  stringFilter: stringFilterProblem,
  inListFilter: inListFilterProblem,
  numericFilter: numericFilterProblem,
  betweenFilter: betweenFilterProblem,
};

function leafProblem(leaf: unknown): string | null {
  if (!isRecord(leaf)) return GA4_REPORT_SENTENCES.filterLeaf;
  const kinds = LEAF_KINDS.filter((kind) => kind in leaf);
  if (
    typeof leaf.fieldName !== "string" ||
    kinds.length !== 1 ||
    !onlyKeys(leaf, ["fieldName", ...LEAF_KINDS])
  ) {
    return GA4_REPORT_SENTENCES.filterLeaf;
  }
  if (!FIELD_NAME_RE.test(leaf.fieldName) || leaf.fieldName.length > GA4_FIELD_NAME_MAX) {
    return GA4_REPORT_SENTENCES.fieldNameRule(leaf.fieldName.slice(0, GA4_FIELD_NAME_MAX));
  }
  const kind = kinds[0]!;
  const body = leaf[kind];
  if (!isRecord(body)) return GA4_REPORT_SENTENCES.filterLeaf;
  return LEAF_BODY_PROBLEM[kind](body);
}

function expressionProblem(expression: unknown, depth: number): string | null {
  if (depth > GA4_FILTER_DEPTH_MAX) return GA4_REPORT_SENTENCES.filterTooDeep;
  if (!isRecord(expression)) return GA4_REPORT_SENTENCES.filterShape;
  const keys = Object.keys(expression);
  if (keys.length !== 1 || !(EXPRESSION_KEYS as readonly string[]).includes(keys[0]!)) {
    return GA4_REPORT_SENTENCES.filterShape;
  }
  const key = keys[0] as (typeof EXPRESSION_KEYS)[number];
  const body = expression[key];
  if (key === "filter") return leafProblem(body);
  if (key === "notExpression") return expressionProblem(body, depth + 1);
  if (!isRecord(body) || !onlyKeys(body, ["expressions"]) || !Array.isArray(body.expressions)) {
    return GA4_REPORT_SENTENCES.filterShape;
  }
  if (body.expressions.length === 0) return GA4_REPORT_SENTENCES.filterShape;
  for (const child of body.expressions) {
    const problem = expressionProblem(child, depth + 1);
    if (problem) return problem;
  }
  return null;
}

/** The one sentence a stored or typed filter object earns, or null when it is a GA4 filter. */
export function ga4FilterProblem(filter: unknown): string | null {
  if (JSON.stringify(filter).length > GA4_FILTER_MAX) return GA4_REPORT_SENTENCES.filterTooLong;
  return expressionProblem(filter, 1);
}

function listProblems(
  field: "dimensions" | "metrics",
  value: unknown,
  max: number,
): { problems: Ga4ReportProblem[]; names: string[] } {
  const problems: Ga4ReportProblem[] = [];
  if (!Array.isArray(value) || !value.every((item) => typeof item === "string")) {
    problems.push({
      field,
      code: "DEFINITION_SHAPE",
      sentence: GA4_REPORT_SENTENCES.definitionShape,
    });
    return { problems, names: [] };
  }
  const names = value as string[];
  problems.push(...countProblems(field, names.length, max));
  problems.push(...nameProblems(field, names));
  return { problems, names };
}

function countProblems(
  field: "dimensions" | "metrics",
  count: number,
  max: number,
): Ga4ReportProblem[] {
  const dimensions = field === "dimensions";
  const problems: Ga4ReportProblem[] = [];
  if (count === 0) {
    problems.push({
      field,
      code: dimensions ? "DIMENSIONS_REQUIRED" : "METRICS_REQUIRED",
      sentence: dimensions
        ? GA4_REPORT_SENTENCES.dimensionsRequired
        : GA4_REPORT_SENTENCES.metricsRequired,
    });
  }
  if (count > max) {
    problems.push({
      field,
      code: dimensions ? "DIMENSIONS_TOO_MANY" : "METRICS_TOO_MANY",
      sentence: dimensions
        ? GA4_REPORT_SENTENCES.dimensionsTooMany
        : GA4_REPORT_SENTENCES.metricsTooMany,
    });
  }
  return problems;
}

function nameProblems(
  field: "dimensions" | "metrics",
  names: readonly string[],
): Ga4ReportProblem[] {
  const problems: Ga4ReportProblem[] = [];
  const seen = new Set<string>();
  for (const name of names) {
    if (name.length > GA4_FIELD_NAME_MAX) {
      problems.push({
        field,
        code: "FIELD_NAME_TOO_LONG",
        sentence: GA4_REPORT_SENTENCES.fieldNameTooLong,
      });
    } else if (!FIELD_NAME_RE.test(name)) {
      problems.push({
        field,
        code: "FIELD_NAME_RULE",
        sentence: GA4_REPORT_SENTENCES.fieldNameRule(name),
      });
    }
    if (seen.has(name)) {
      problems.push({
        field,
        code: "FIELD_REPEATED",
        sentence: GA4_REPORT_SENTENCES.repeated(name),
      });
    }
    seen.add(name);
  }
  return problems;
}

/**
 * Every problem a report earns, one sentence per finding, in field order. An empty list means the
 * report saves. `definition` is whatever the request carried (unknown on the server); `name` is the
 * report's own name and `context.otherNames` the connection's other reports.
 */
export function ga4ReportProblems(
  name: string,
  definition: unknown,
  context: Ga4ReportContext,
): Ga4ReportProblem[] {
  const problems: Ga4ReportProblem[] = nameProblemsOf(name, context);
  if (!isRecord(definition)) {
    problems.push({
      field: "propertyId",
      code: "DEFINITION_SHAPE",
      sentence: GA4_REPORT_SENTENCES.definitionShape,
    });
    return problems;
  }
  problems.push(...shapeProblems(definition), ...propertyProblems(definition, context));

  const dimensions = listProblems("dimensions", definition.dimensions, GA4_DIMENSIONS_MAX);
  const metrics = listProblems("metrics", definition.metrics, GA4_METRICS_MAX);
  problems.push(...dimensions.problems, ...metrics.problems);
  problems.push(...columnProblems(dimensions.names, metrics.names));

  if (definition.dimensionFilter !== undefined && definition.dimensionFilter !== null) {
    const problem = ga4FilterProblem(definition.dimensionFilter);
    if (problem) {
      problems.push({ field: "dimensionFilter", code: "FILTER_INVALID", sentence: problem });
    }
  }

  if (problems.length === 0 && definitionTooLarge(definition)) {
    problems.push({
      field: "dimensionFilter",
      code: "DEFINITION_TOO_LARGE",
      sentence: GA4_REPORT_SENTENCES.definitionTooLarge,
    });
  }
  return problems;
}

function nameProblemsOf(name: string, context: Ga4ReportContext): Ga4ReportProblem[] {
  if (!REPORT_NAME_RE.test(name)) {
    return [{ field: "name", code: "NAME_RULE", sentence: GA4_REPORT_SENTENCES.nameRule }];
  }
  const segment = entityTableSegment(name);
  return context.otherNames.some((other) => entityTableSegment(other) === segment)
    ? [{ field: "name", code: "NAME_TAKEN", sentence: GA4_REPORT_SENTENCES.nameTaken }]
    : [];
}

const DEFINITION_KEYS = new Set(["kind", "propertyId", "dimensions", "metrics", "dimensionFilter"]);

function shapeProblems(definition: Record<string, unknown>): Ga4ReportProblem[] {
  const unknownKey = Object.keys(definition).some((key) => !DEFINITION_KEYS.has(key));
  const wrongKind = definition.kind !== undefined && definition.kind !== GA4_REPORT_KIND;
  return unknownKey || wrongKind
    ? [
        {
          field: "propertyId",
          code: "DEFINITION_SHAPE",
          sentence: GA4_REPORT_SENTENCES.definitionShape,
        },
      ]
    : [];
}

function propertyProblems(
  definition: Record<string, unknown>,
  context: Ga4ReportContext,
): Ga4ReportProblem[] {
  const propertyId = definition.propertyId;
  if (typeof propertyId !== "string" || propertyId.trim() === "") {
    return [
      {
        field: "propertyId",
        code: "PROPERTY_REQUIRED",
        sentence: GA4_REPORT_SENTENCES.propertyRequired,
      },
    ];
  }
  if (!PROPERTY_ID_RE.test(propertyId)) {
    return [
      { field: "propertyId", code: "PROPERTY_RULE", sentence: GA4_REPORT_SENTENCES.propertyRule },
    ];
  }
  if (!context.propertyIds.includes(propertyId)) {
    return [
      {
        field: "propertyId",
        code: "PROPERTY_NOT_LISTED",
        sentence: GA4_REPORT_SENTENCES.propertyNotListed(propertyId),
      },
    ];
  }
  return [];
}

/**
 * A metric also listed as a dimension, and (R25) two names that fold to one column
 * (`customEvent:Plan`, `customEvent:plan`) or a name that folds to one of the image's own columns,
 * which the load would rename silently.
 */
function columnProblems(dimensionNames: string[], metricNames: string[]): Ga4ReportProblem[] {
  const problems: Ga4ReportProblem[] = [];
  const dimensionSet = new Set(dimensionNames);
  for (const metric of new Set(metricNames)) {
    if (dimensionSet.has(metric)) {
      problems.push({
        field: "metrics",
        code: "FIELD_IN_BOTH",
        sentence: GA4_REPORT_SENTENCES.inBoth(metric),
      });
    }
  }
  const reserved = new Map(LOAD_COLUMNS.map((column) => [ga4ColumnOf(column), column]));
  const columnOwner = new Map<string, string>();
  const columns: Array<["dimensions" | "metrics", string]> = [
    ...[...dimensionSet].map((n) => ["dimensions", n] as ["dimensions", string]),
    ...[...new Set(metricNames)]
      .filter((n) => !dimensionSet.has(n))
      .map((n) => ["metrics", n] as ["metrics", string]),
  ];
  for (const [field, columnName] of columns) {
    if (!FIELD_NAME_RE.test(columnName)) continue;
    const column = ga4ColumnOf(columnName);
    const owner = columnOwner.get(column);
    if (reserved.has(column)) {
      problems.push({
        field,
        code: "RESERVED_COLUMN",
        sentence: GA4_REPORT_SENTENCES.reservedColumn(columnName),
      });
    } else if (owner === undefined) {
      columnOwner.set(column, columnName);
    } else {
      problems.push({
        field,
        code: "COLUMN_CLASH",
        sentence: GA4_REPORT_SENTENCES.columnClash(owner, columnName),
      });
    }
  }
  return problems;
}

function definitionTooLarge(definition: Record<string, unknown>): boolean {
  const bytes = new TextEncoder().encode(JSON.stringify(normaliseGa4Definition(definition)));
  return bytes.length > GA4_DEFINITION_BYTES_MAX;
}

/**
 * The stored form of a definition that has no problems: `kind` first, the lists in the person's
 * order (the image writes columns in that order), and no `dimensionFilter` key for an empty one.
 */
export function normaliseGa4Definition(definition: Record<string, unknown>): Ga4ReportDefinition {
  const filter = definition.dimensionFilter;
  const out: Ga4ReportDefinition = {
    kind: GA4_REPORT_KIND,
    propertyId: String(definition.propertyId),
    dimensions: [...(definition.dimensions as string[])],
    metrics: [...(definition.metrics as string[])],
  };
  if (isRecord(filter) && Object.keys(filter).length > 0) out.dimensionFilter = filter;
  return out;
}

/** Narrows a stored jsonb value to a definition, or null when it is not one. */
export function storedGa4Definition(value: unknown): Ga4ReportDefinition | null {
  if (!isRecord(value) || value.kind !== GA4_REPORT_KIND) return null;
  if (
    typeof value.propertyId !== "string" ||
    !Array.isArray(value.dimensions) ||
    !Array.isArray(value.metrics)
  ) {
    return null;
  }
  return value as unknown as Ga4ReportDefinition;
}

// ---------------------------------------------------------------------------------------------
// The filter builder: flat conditions joined by all or any, a Not tick per condition (R9).
// ---------------------------------------------------------------------------------------------

export const GA4_FILTER_OPERATORS = [
  "exact",
  "begins_with",
  "ends_with",
  "contains",
  "full_regexp",
  "in_list",
  "num_equal",
  "num_greater",
  "num_less",
  "between",
] as const;
export type Ga4FilterOperator = (typeof GA4_FILTER_OPERATORS)[number];

/** The operator's words, as the builder and the summary say them. */
export const GA4_FILTER_OPERATOR_WORDS: Record<Ga4FilterOperator, string> = {
  exact: "is exactly",
  begins_with: "begins with",
  ends_with: "ends with",
  contains: "contains",
  full_regexp: "matches the pattern",
  in_list: "is one of",
  num_equal: "is",
  num_greater: "is more than",
  num_less: "is less than",
  between: "is between",
};

const STRING_OPERATORS: Partial<Record<Ga4FilterOperator, Ga4MatchType>> = {
  exact: "EXACT",
  begins_with: "BEGINS_WITH",
  ends_with: "ENDS_WITH",
  contains: "CONTAINS",
  full_regexp: "FULL_REGEXP",
};
const NUMERIC_OPERATORS: Partial<Record<Ga4FilterOperator, string>> = {
  num_equal: "EQUAL",
  num_greater: "GREATER_THAN",
  num_less: "LESS_THAN",
};

export interface Ga4FilterCondition {
  fieldName: string;
  operator: Ga4FilterOperator;
  /** The value; for `in_list` the values one per line or comma; for `between` the lower bound. */
  value: string;
  /** `between` only: the upper bound. */
  to: string;
  not: boolean;
  caseSensitive: boolean;
}

export interface Ga4FilterBuilder {
  join: "all" | "any";
  conditions: Ga4FilterCondition[];
}

export function emptyGa4Condition(): Ga4FilterCondition {
  return { fieldName: "", operator: "exact", value: "", to: "", not: false, caseSensitive: false };
}

function numericValueOf(text: string): Record<string, unknown> | null {
  const trimmed = text.trim();
  if (/^-?[0-9]{1,19}$/.test(trimmed)) return { int64Value: trimmed };
  if (trimmed !== "" && Number.isFinite(Number(trimmed))) return { doubleValue: Number(trimmed) };
  return null;
}

function numericText(value: unknown): string | null {
  if (!isRecord(value)) return null;
  if (typeof value.int64Value === "string") return value.int64Value;
  if (typeof value.doubleValue === "number") return String(value.doubleValue);
  return null;
}

function conditionLeaf(condition: Ga4FilterCondition): Record<string, unknown> | null {
  const fieldName = condition.fieldName.trim();
  if (fieldName === "") return null;
  const matchType = STRING_OPERATORS[condition.operator];
  let leaf: Record<string, unknown>;
  if (matchType) {
    leaf = {
      fieldName,
      stringFilter: {
        matchType,
        value: condition.value,
        ...(condition.caseSensitive ? { caseSensitive: true } : {}),
      },
    };
  } else if (condition.operator === "in_list") {
    leaf = {
      fieldName,
      inListFilter: {
        values: splitGa4NameList(condition.value),
        ...(condition.caseSensitive ? { caseSensitive: true } : {}),
      },
    };
  } else if (condition.operator === "between") {
    const fromValue = numericValueOf(condition.value);
    const toValue = numericValueOf(condition.to);
    if (!fromValue || !toValue) return null;
    leaf = { fieldName, betweenFilter: { fromValue, toValue } };
  } else {
    const value = numericValueOf(condition.value);
    if (!value) return null;
    leaf = {
      fieldName,
      numericFilter: { operation: NUMERIC_OPERATORS[condition.operator], value },
    };
  }
  const expression = { filter: leaf };
  return condition.not ? { notExpression: expression } : expression;
}

/**
 * ClaudeBeta review 6: the first condition the builder could not write, in a sentence naming it by
 * its number, or null. `ga4FilterFromBuilder` leaves such a condition out, so the drawer refuses
 * Add while one stands rather than saving a filter that reads more rows than the person asked for.
 */
export function ga4BuilderProblem(builder: Ga4FilterBuilder): string | null {
  for (const [index, condition] of builder.conditions.entries()) {
    const number = index + 1;
    if (condition.fieldName.trim() === "") {
      const blank = condition.value.trim() === "" && condition.to.trim() === "";
      return blank
        ? `Condition ${number} is empty. Fill it in or remove it.`
        : `Condition ${number} needs a field.`;
    }
    if (conditionLeaf(condition) === null) {
      return condition.operator === "between"
        ? `Condition ${number} needs a number in From and in To.`
        : `Condition ${number} needs a number.`;
    }
  }
  return null;
}

/**
 * The builder's conditions as a GA4 filter, or null when there is none (no condition with a
 * field). A condition whose number does not parse is left out, and the drawer says so beside it.
 */
export function ga4FilterFromBuilder(builder: Ga4FilterBuilder): Ga4FilterExpression | null {
  const expressions = builder.conditions
    .map(conditionLeaf)
    .filter((leaf): leaf is Record<string, unknown> => leaf !== null);
  if (expressions.length === 0) return null;
  if (expressions.length === 1) return expressions[0]!;
  return builder.join === "all" ? { andGroup: { expressions } } : { orGroup: { expressions } };
}

type ConditionBase = Pick<Ga4FilterCondition, "fieldName" | "not">;

function stringConditionOf(base: ConditionBase, body: Record<string, unknown>) {
  const operator = (Object.keys(STRING_OPERATORS) as Ga4FilterOperator[]).find(
    (key) => STRING_OPERATORS[key] === body.matchType,
  );
  if (!operator || typeof body.value !== "string") return null;
  return {
    ...emptyGa4Condition(),
    ...base,
    operator,
    value: body.value,
    caseSensitive: body.caseSensitive === true,
  };
}

function inListConditionOf(base: ConditionBase, body: Record<string, unknown>) {
  if (!Array.isArray(body.values)) return null;
  return {
    ...emptyGa4Condition(),
    ...base,
    operator: "in_list" as const,
    value: (body.values as unknown[]).map(String).join("\n"),
    caseSensitive: body.caseSensitive === true,
  };
}

function numericConditionOf(base: ConditionBase, body: Record<string, unknown>) {
  const operator = (Object.keys(NUMERIC_OPERATORS) as Ga4FilterOperator[]).find(
    (key) => NUMERIC_OPERATORS[key] === body.operation,
  );
  const value = numericText(body.value);
  if (!operator || value === null) return null;
  return { ...emptyGa4Condition(), ...base, operator, value };
}

function betweenConditionOf(base: ConditionBase, body: Record<string, unknown>) {
  const from = numericText(body.fromValue);
  const to = numericText(body.toValue);
  if (from === null || to === null) return null;
  return { ...emptyGa4Condition(), ...base, operator: "between" as const, value: from, to };
}

const CONDITION_OF: ReadonlyArray<
  [string, (base: ConditionBase, body: Record<string, unknown>) => Ga4FilterCondition | null]
> = [
  ["stringFilter", stringConditionOf],
  ["inListFilter", inListConditionOf],
  ["numericFilter", numericConditionOf],
  ["betweenFilter", betweenConditionOf],
];

function conditionOf(expression: unknown): Ga4FilterCondition | null {
  if (!isRecord(expression)) return null;
  const not = "notExpression" in expression && Object.keys(expression).length === 1;
  const inner: unknown = not ? expression.notExpression : expression;
  if (!isRecord(inner) || Object.keys(inner).length !== 1 || !isRecord(inner.filter)) return null;
  const leaf = inner.filter;
  const base = { fieldName: typeof leaf.fieldName === "string" ? leaf.fieldName : "", not };
  for (const [kind, read] of CONDITION_OF) {
    const body = leaf[kind];
    if (isRecord(body)) return read(base, body);
  }
  return null;
}

function groupOf(filter: Ga4FilterExpression): Record<string, unknown> | null {
  if (isRecord(filter.andGroup)) return filter.andGroup;
  if (isRecord(filter.orGroup)) return filter.orGroup;
  return null;
}

function builderOf(filter: Ga4FilterExpression): Ga4FilterBuilder | null {
  const group = groupOf(filter);
  if (group && Object.keys(filter).length === 1 && Array.isArray(group.expressions)) {
    const conditions = group.expressions.map(conditionOf);
    if (!conditions.every((c): c is Ga4FilterCondition => c !== null)) return null;
    return { join: filter.andGroup ? "all" : "any", conditions };
  }
  const condition = conditionOf(filter);
  return condition ? { join: "all", conditions: [condition] } : null;
}

/**
 * The filter as builder conditions, or null when the builder cannot show it without loss (nested
 * groups, a PARTIAL_REGEXP, an explicit `caseSensitive: false`, an operator the builder does not
 * offer): such a filter stays JSON ("This filter is edited as JSON.").
 */
export function ga4BuilderFromFilter(filter: Ga4FilterExpression | null): Ga4FilterBuilder | null {
  if (filter === null) return { join: "all", conditions: [] };
  const builder = builderOf(filter);
  if (!builder) return null;
  // A stored filter comes back from jsonb with its keys reordered, so the round trip compares as
  // jsonb does, never by text.
  return sameJsonb(ga4FilterFromBuilder(builder), filter) ? builder : null;
}

const NEGATED_WORDS: Record<Ga4FilterOperator, string> = {
  exact: "is not exactly",
  begins_with: "does not begin with",
  ends_with: "does not end with",
  contains: "does not contain",
  full_regexp: "does not match the pattern",
  in_list: "is not one of",
  num_equal: "is not",
  num_greater: "is not more than",
  num_less: "is not less than",
  between: "is not between",
};

/** The builder's option label for each operator (R9's words). */
export const GA4_FILTER_OPERATOR_LABELS: Record<Ga4FilterOperator, string> = {
  exact: "is exactly",
  begins_with: "begins with",
  ends_with: "ends with",
  contains: "contains",
  full_regexp: "matches a pattern",
  in_list: "is one of",
  num_equal: "number is",
  num_greater: "number is more than",
  num_less: "number is less than",
  between: "number is between",
};

function conditionValueWords(condition: Ga4FilterCondition): string {
  if (condition.operator === "in_list") return splitGa4NameList(condition.value).join(", ");
  if (condition.operator === "between") return `${condition.value} and ${condition.to}`;
  return condition.value;
}

function conditionSentence(condition: Ga4FilterCondition): string {
  const words = (condition.not ? NEGATED_WORDS : GA4_FILTER_OPERATOR_WORDS)[condition.operator];
  // #2482: Match case changes which rows the report keeps, so the line says it.
  const matchCase = condition.caseSensitive === true ? " (match case)" : "";
  return `${condition.fieldName} ${words} ${conditionValueWords(condition)}${matchCase}`.trim();
}

/** The list's one line for a filter: "None", a sentence, or "Custom filter" for one kept as JSON. */
export function ga4FilterSummary(filter: Ga4FilterExpression | null | undefined): string {
  if (!filter) return "None";
  const builder = ga4BuilderFromFilter(filter);
  if (!builder || builder.conditions.length === 0) return "Custom filter";
  const joiner = builder.join === "all" ? " and " : " or ";
  return builder.conditions.map(conditionSentence).join(joiner);
}

/** The drawer's hints for a report, each a sentence, shown only when they apply. */
export function ga4ReportHints(
  dimensions: readonly string[],
  metrics: readonly string[],
): string[] {
  const hints: string[] = [];
  if (dimensions.includes("month") && !dimensions.includes("yearMonth")) {
    hints.push("month is 01 to 12 with no year. Use yearMonth to tell the years apart.");
  }
  const users = metrics.filter((m) => ["totalUsers", "newUsers", "activeUsers"].includes(m));
  if (users.length > 0) {
    hints.push(
      `${users.join(", ")} count people per day, so the days do not add up to a month. sessions and eventCount do add up.`,
    );
  }
  return hints;
}

/**
 * R24: about how many requests a Full refresh of these reports makes, Google's own count per
 * report per day, plus two per property for its metadata and time zone.
 */
export function ga4FullRefreshRequests(reports: number, days: number, properties: number): number {
  return reports * days + 2 * properties;
}
